import { useEffect } from 'react';
import { useQuery } from 'react-query';
import useLocalStorage from 'react-use/lib/useLocalStorage.js';
import { addHours } from 'date-fns';

import useOrgId from 'hooks/useOrgId.js';
import useOrganizations from 'hooks/useOrganizations.js';
import usePermissions from 'hooks/usePermissions.jsx';
import useSocialMediaService from 'hooks/useSocialMediaService.jsx';
import { ON_DEMAND_PRODUCT } from 'constants/products.js';
import { trackingService } from 'services/tracking/TrackingService.js';

const TTL_HOURS = 6;
const TTL_MS = TTL_HOURS * 60 * 60 * 1000;

const INITIAL_STORAGE_VALUE = {
  hasUgcConnection: false,
  timestamp: 0,
};

const usePendoUgcEligibility = () => {
  const orgId = useOrgId();
  const { data: organizations = [], isFetched: organizationsFetched } = useOrganizations();
  const currentOrganization = organizations.find(({ id }) => id === orgId);
  const { hasOrgSpecificProductPermission } = usePermissions();
  const socialMediaService = useSocialMediaService(orgId);

  const hasOnDemandProduct =
    !!currentOrganization?.features?.vod?.enabled &&
    !!hasOrgSpecificProductPermission(orgId, ON_DEMAND_PRODUCT);

  const storageKey = `pendo-ugc-eligibility-${orgId}`;
  const [storage, setStorage] = useLocalStorage(storageKey, INITIAL_STORAGE_VALUE);

  useQuery({
    enabled: !!socialMediaService && !!orgId && organizationsFetched && hasOnDemandProduct,
    queryKey: ['pendo-ugc-eligibility', orgId],
    staleTime: TTL_MS,
    initialData: () => {
      const isExpired = addHours(new Date(storage.timestamp), TTL_HOURS) < new Date();
      return isExpired ? undefined : storage.hasUgcConnection;
    },
    initialDataUpdatedAt: storage.timestamp ? new Date(storage.timestamp).getTime() : 0,
    queryFn: async () => {
      const pages = await socialMediaService.getConnectedPages('ugc', ON_DEMAND_PRODUCT);
      return Array.isArray(pages) && pages.length > 0;
    },
    onSuccess: (hasUgcConnection) => {
      setStorage({ hasUgcConnection: !!hasUgcConnection, timestamp: new Date() });
      trackingService.track({ eventType: 'identify' }, { Pendo: true });
    },
    onError: (error) => {
      console.error('Failed to fetch UGC connections for Pendo eligibility', error?.message);
      setStorage({ hasUgcConnection: false, timestamp: new Date() });
    },
  });

  useEffect(() => {
    if (!orgId || !organizationsFetched) return;
    if (!hasOnDemandProduct && storage.hasUgcConnection) {
      setStorage({ hasUgcConnection: false, timestamp: new Date() });
      trackingService.track({ eventType: 'identify' }, { Pendo: true });
    }
  }, [orgId, organizationsFetched, hasOnDemandProduct, storage.hasUgcConnection, setStorage]);
};

export default usePendoUgcEligibility;
